import api from './api';

// Helper function to check authentication
const checkAuth = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required. Please sign in.');
  }
  return token;
};

export const createDividend = async (dividendData) => {
  try {
    checkAuth(); // Check authentication before making API call
    
    const response = await api.post('/api/data_predator/dividends/', dividendData);
    return response.data;
  } catch (error) {
    console.error('Error creating dividend:', error);
    throw error;
  }
};

export const getDividendData = async (filters = {}) => {
  try {
    checkAuth(); // Check authentication before making API call
    
    const params = new URLSearchParams();
    
    // Add filters to query parameters
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        // Handle multiple stock names
        if (key === 'stock_name' && Array.isArray(filters[key])) {
          filters[key].forEach(stockName => {
            params.append('stock_name', stockName);
          });
        }
        // Handle multiple account names
        else if (key === 'account_name' && Array.isArray(filters[key])) {
          filters[key].forEach(accountName => {
            params.append('account_name', accountName);
          });
        } else {
          params.append(key, filters[key]);
        }
      }
    });

    const response = await api.get(`/api/data_predator/dividends/?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching dividend data:', error);
    throw error;
  }
};

// Helper function to process dividend data for monthly chart
export const processDividendDataForChart = (dividendData) => {
  if (!dividendData || !dividendData.results || !Array.isArray(dividendData.results)) {
    return { categories: [], series: [], monthlyDetails: {} };
  }

  // Group dividends by month
  const monthlyData = {};
  
  dividendData.results.forEach(dividend => {
    // Add safety checks for required fields
    if (!dividend || !dividend.dividend_date || !dividend.dividend_amount || !dividend.quantity) {
      return; // Skip invalid records
    }

    const date = new Date(dividend.dividend_date);
    if (isNaN(date.getTime())) {
      return; // Skip invalid dates
    }

    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        total: 0,
        totalAmount: 0,
        count: 0,
        accounts: {},
        stocks: {}
      };
    }
    
    const dividendAmount = parseFloat(dividend.dividend_amount) || 0;
    const quantity = parseInt(dividend.quantity) || 0;
    const totalAmount = dividendAmount * quantity;
    
    monthlyData[monthKey].total += dividendAmount;
    monthlyData[monthKey].totalAmount += totalAmount;
    monthlyData[monthKey].count += 1;
    
    // Group by account for detailed view
    const accountName = dividend.account_name || 'Unknown Account';
    if (!monthlyData[monthKey].accounts[accountName]) {
      monthlyData[monthKey].accounts[accountName] = {
        dividendAmount: 0,
        totalAmount: 0
      };
    }
    monthlyData[monthKey].accounts[accountName].dividendAmount += dividendAmount;
    monthlyData[monthKey].accounts[accountName].totalAmount += totalAmount;
    
    // Group by stock for detailed view
    const stockName = dividend.stock_name || 'Unknown';
    if (!monthlyData[monthKey].stocks[stockName]) {
      monthlyData[monthKey].stocks[stockName] = {
        dividendAmount: 0,
        totalAmount: 0
      };
    }
    monthlyData[monthKey].stocks[stockName].dividendAmount += dividendAmount;
    monthlyData[monthKey].stocks[stockName].totalAmount += totalAmount;
  });

  // Sort months chronologically
  const sortedMonths = Object.keys(monthlyData).sort();
  
  // Calculate month-over-month changes
  sortedMonths.forEach((month, index) => {
    if (index > 0) {
      const currentMonth = monthlyData[month];
      const previousMonth = monthlyData[sortedMonths[index - 1]];
      
      // Calculate total amount change
      const totalAmountChange = currentMonth.totalAmount - previousMonth.totalAmount;
      const totalAmountChangePercent = previousMonth.totalAmount !== 0 
        ? ((totalAmountChange / previousMonth.totalAmount) * 100) 
        : (currentMonth.totalAmount > 0 ? 100 : 0);
      
      // Add comparison data to current month
      monthlyData[month].comparison = {
        totalAmountChange,
        totalAmountChangePercent,
        previousMonthTotalAmount: previousMonth.totalAmount
      };
    } else {
      // First month has no comparison
      monthlyData[month].comparison = {
        totalAmountChange: 0,
        totalAmountChangePercent: 0,
        previousMonthTotalAmount: 0
      };
    }
  });
  
  const categories = sortedMonths.map(month => {
    const [year, monthNum] = month.split('-');
    return `${year}-${monthNum}`;
  });
  
  const totalAmounts = sortedMonths.map(month => monthlyData[month]?.total || 0);
  const totalDividendAmounts = sortedMonths.map(month => monthlyData[month]?.totalAmount || 0);

  return {
    categories: categories || [],
    series: [
      {
        name: 'Total Dividend Amount',
        data: totalDividendAmounts || [],
        type: 'column'
      },
      {
        name: 'Dividend Amount (Per Share)',
        data: totalAmounts || [],
        type: 'line'
      }
    ],
    monthlyDetails: monthlyData
  };
};
